import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../store";
import { getAuthState, logout } from "../store/slices/authSlice";
import Api from "../services/apis/setup";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

function Settings() {
  const { user } = useAppSelector(getAuthState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [updateProfile] = Api.useUpdateProfileMutation();
  const [deleteAccount] = Api.useDeleteAccountMutation();

  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState(user?.picture || "");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setImagePreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to crop and resize image
  const cropAndResizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for cropping/resizing
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          // Set dimensions (150x150 for profile pictures)
          const size = 150;
          canvas.width = size;
          canvas.height = size;

          // Draw cropped/resized image
          ctx.drawImage(img, 0, 0, size, size);

          // Convert to data URL
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let pictureData = imagePreview;

      // If a new image was selected, crop and resize it
      if (profileImage) {
        try {
          pictureData = await cropAndResizeImage(profileImage);
        } catch (error) {
          console.error("Error processing image:", error);
          // If image processing fails, use the original preview
        }
      }

      const userData = {
        name: formData.name,
        picture: pictureData
      };

      // Update user profile via API
      const response = await updateProfile({
        baseURL: "/user/profile",
        method: "PUT",
        payload: JSON.stringify(userData),
      });

      const toastId = "profile-update-toast";

      if (response && 'data' in response) {
        toast.success("Profile updated successfully!", { toastId });
      }

      if (response && 'error' in response) {
        console.error("Error updating profile:", response.error);
        toast.error("Error updating profile. Please try again.", { toastId });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error updating profile. Please try again.", { toastId: "profile-update-toast" });
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteAccount({
        baseURL: "/user/account",
        method: "DELETE",
      } as any);

      const toastId = "account-delete-toast";

      if (response && 'data' in response) {
        // Account deletion successful, user is automatically logged out
        toast.success("Account deleted successfully!", { toastId });
        navigate("/login");
      }

      if (response && 'error' in response) {
        console.error("Error deleting account:", response.error);
        toast.error("Error deleting account. Please try again.", { toastId });
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Error deleting account. Please try again.", { toastId: "account-delete-toast" });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Account Settings
      </Typography>
      <Box sx={{ maxWidth: 600, mx: "auto", mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={12} sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Box sx={{ position: "relative" }}>
                  <Avatar
                    src={imagePreview}
                    sx={{ width: 100, height: 100, mb: 2 }}
                  />
                  <IconButton
                    color="primary"
                    aria-label="upload picture"
                    component="label"
                    sx={{ position: "absolute", bottom: 10, right: -10 }}
                  >
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      onChange={handleImageChange}
                    />
                    <PhotoCamera />
                  </IconButton>
                </Box>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                        borderWidth: 1,
                      },
                    },
                    '& .MuiInputBase-input': {
                      '&:focus': {
                        outline: 'none',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ backgroundColor: 'var(--primary-color)', color: 'white' }}
                  >
                    Update Profile
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Delete Account Section */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Delete Account
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Account
          </Button>
        </Paper>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-account-dialog-title"
        aria-describedby="delete-account-dialog-description"
      >
        <DialogTitle id="delete-account-dialog-title">
          Confirm Account Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-account-dialog-description">
            Are you sure you want to delete your account? This will permanently remove:
            <ul>
              <li>Your user profile</li>
              <li>All your vehicles and their service history</li>
              <li>All associated data</li>
            </ul>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete Account"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Settings;